import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Compass,
    AlertTriangle,
    BookOpen,
    HeartHandshake,
    Home,
    Shield,
    Sparkles,
    ArrowRight,
    Info,
} from "lucide-react";
import { IMG, BTN } from "@/lib/design";

const fade = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const SERVE = [
    {
        icon: AlertTriangle,
        title: "Young Men Facing Behavioral Challenges",
        body: "Support for youth struggling with emotional regulation, impulsivity, aggression, defiance, or other behaviors impacting stability and daily functioning.",
    },
    {
        icon: HeartHandshake,
        title: "Young Men Impacted by Trauma",
        body: "Relationship-based and trauma-informed care for youth navigating grief, loss, instability, abuse, neglect, or difficult life experiences.",
    },
    {
        icon: Home,
        title: "Young Men Needing Stability",
        body: "A structured and supportive environment for youth experiencing placement disruptions, instability, or difficulty maintaining consistency across settings.",
    },
    {
        icon: Sparkles,
        title: "Young Men Seeking Emotional Growth",
        body: "Support focused on emotional regulation, healthy coping skills, communication, and building stronger relationships.",
    },
    {
        icon: Shield,
        title: "Young Men Needing Therapeutic Support",
        body: "Coordination of therapy, psychiatric services, medication management, and behavioral support tailored to each youth's needs.",
    },
    {
        icon: BookOpen,
        title: "Young Men Building Life Skills",
        body: "Opportunities for youth to develop responsibility, independence, decision-making, conflict resolution, and daily living skills.",
    },
    {
        icon: Compass,
        title: "Young Men Preparing for the Future",
        body: "Support for transitional-aged youth working toward stability, education, employment readiness, and long-term success.",
    },
];

export default function WhoWeServe() {
    return (
        <div data-testid="who-page">
            {/* HERO */}
            <section className="bg-[#F7F5F0] pt-16 md:pt-24 pb-12">
                <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-14">
                    <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">
                        Who We Serve
                    </p>
                    <h1 className="mt-3 font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
                        Supporting Young Men Through Healing, Growth, and Change.
                    </h1>
                    <p className="mt-6 text-[17px] leading-relaxed text-[#6F6B63] max-w-3xl">
                        Crossroads to Change Residential Treatment Center
                        supports young men impacted by trauma, behavioral
                        challenges, and instability by providing a safe,
                        structured, and therapeutic environment focused on
                        healing, accountability, emotional growth, and
                        long-term success.
                    </p>
                </div>
            </section>

            {/* SERVE LIST — de-boxed clean layout */}
            <section className="py-20 md:py-28 bg-[#F7F5F0]">
                <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-14">
                    <div className="space-y-12 md:space-y-16">
                        {SERVE.map((s, i) => (
                            <motion.div
                                key={s.title}
                                {...fade}
                                transition={{ ...fade.transition, delay: i * 0.04 }}
                                className="grid grid-cols-[auto_1fr] gap-5 md:gap-8 items-start pb-12 md:pb-16 border-b border-[#E5DFD3] last:border-0 last:pb-0"
                                data-testid={`serve-card-${i}`}
                            >
                                <div className="h-12 w-12 md:h-14 md:w-14 rounded-2xl bg-[#0E2A47]/5 grid place-items-center text-[#0E2A47] shrink-0">
                                    <s.icon className="h-5.5 w-5.5 md:h-6 md:w-6" />
                                </div>
                                <div className="pt-1">
                                    <h3 className="font-[Outfit] text-[20px] md:text-[22px] font-semibold tracking-tight text-[#1A2230]">
                                        {s.title}
                                    </h3>
                                    <p className="mt-3 text-[15px] md:text-[16px] leading-relaxed text-[#6F6B63] max-w-2xl">
                                        {s.body}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* IMAGE + ADMISSIONS NOTE */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-6">
                        <div className="rounded-3xl overflow-hidden aspect-[5/4]">
                            <img
                                src={IMG.community}
                                alt="Diverse hands joined together"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                    <div className="lg:col-span-6">
                        <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">
                            A case-by-case approach
                        </p>
                        <h2 className="mt-3 font-[Outfit] text-3xl md:text-4xl font-semibold tracking-tight">
                            We listen first — then decide together.
                        </h2>
                        <p className="mt-5 text-[16px] leading-relaxed text-[#6F6B63]">
                            Every young man is more than a checklist. When you
                            reach out, our team will gather the picture, ask
                            honest questions and help determine whether our
                            program is the right next step.
                        </p>
                        <div className="mt-7 flex items-start gap-3 p-5 rounded-2xl bg-[#EFEBE0] border border-[#E5DFD3]">
                            <Info className="h-5 w-5 text-[#0E2A47] mt-0.5 shrink-0" />
                            <p className="text-[14.5px] text-[#1A2230]/85 leading-relaxed">
                                Admissions are reviewed on a case-by-case
                                basis to ensure the program is an appropriate
                                fit for each youth.
                            </p>
                        </div>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/referrals" className={BTN.primary} data-testid="who-refer-cta">
                                Start a referral <ArrowRight className="h-4 w-4" />
                            </Link>
                            <Link to="/contact" className={BTN.outline} data-testid="who-contact-cta">
                                Talk with our team
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
